import React, { useEffect, useMemo, useRef, useState } from "react";
import AstriaRingLogo from "../astria/AstriaRingLogo";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CloudRain, Crown, Heart } from "lucide-react";
import { api } from "../../services/api";
import ReactMarkdown from "react-markdown";
import { useSearchParams } from "react-router-dom";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";
import { serverErrorMessage } from "../../utils/serverError";

type ForecastSeries = {
  label: string;
  color: string;
  data: number[];
  peak_point?: {
    month: string;
    value: number;
    title: string;
    desc: string;
  };
  low_point?: {
    month: string;
    value: number;
    title: string;
    desc: string;
  };
};

type ForecastResponse = {
  status: "success" | "error";
  data: {
    year_th: string;
    user_name: string;
    overall_theme: string;
    graph_data: {
      months_labels: string[];
      series: ForecastSeries[];
    };
    yearly_forecast: {
      peak_period: string;
      obstacle_period: string;
      auspicious_colors: string[];
      lucky_numbers: number[];
      deities_to_worship: string[];
    };
  };
};

const fallbackResponse: ForecastResponse = {
  status: "success",
  data: {
    year_th: "๒๕๖๙",
    user_name: `คุณ Astria User`,
    overall_theme: "ปีแห่งการเติบโตและการเริ่มต้นใหม่",
    graph_data: {
      months_labels: [
        "ม.ค.",
        "ก.พ.",
        "มี.ค.",
        "เม.ย.",
        "พ.ค.",
        "มิ.ย.",
        "ก.ค.",
        "ส.ค.",
        "ก.ย.",
        "ต.ค.",
        "พ.ย.",
        "ธ.ค.",
      ],
      series: [
        {
          label: "การงาน/โชคลาภ",
          color: "#FFD700",
          data: [40, 50, 60, 95, 70, 55, 50, 45, 60, 75, 80, 85],
          peak_point: {
            month: "เม.ย.",
            value: 95,
            title: "มงกุฎทอง",
            desc: "ดวงพุ่งแรงที่สุด! เหมาะกับการเริ่มโปรเจกต์ใหม่",
          },
        },
        {
          label: "ความรัก",
          color: "#FF69B4",
          data: [60, 85, 70, 65, 60, 75, 80, 60, 55, 50, 65, 70],
          peak_point: {
            month: "ก.พ.",
            value: 85,
            title: "ความรักเบ่งบาน",
            desc: "ช่วงเวลาที่ดีที่สุดสำหรับความสัมพันธ์",
          },
        },
        {
          label: "พลังกาย/ใจ",
          color: "#00CED1",
          data: [70, 65, 60, 55, 50, 45, 40, 30, 45, 55, 60, 65],
          low_point: {
            month: "ส.ค.",
            value: 30,
            title: "ก้อนเมฆและฝน",
            desc: "ช่วงพลังใจดรอป ให้กำลังใจตัวเอง ลองทำบุญแก้เคล็ด",
          },
        },
      ],
    },
    yearly_forecast: {
      peak_period: "เมษายน - มิถุนายน: เริ่มต้นสิ่งใหม่ ลุยงานเต็มที่!",
      obstacle_period: "สิงหาคม: ให้กำลังใจตัวเอง ปล่อยนกปล่อยปลาแก้เคล็ด",
      auspicious_colors: ["เหลือง", "ชมพู"],
      lucky_numbers: [7, 9],
      deities_to_worship: ["พระพิฆเนศ", "ท้าวเวสสุวรรณ"],
    },
  },
};

type Insight = {
  title: string;
  desc: string;
  color: string;
  month: string;
  icon: "crown" | "cloud" | "heart";
  seriesLabel?: string;
};

const LifeGraph: React.FC = () => {
  const { t } = useTranslation();
  const [apiResponse, setApiResponse] = useState<ForecastResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data } = apiResponse ?? fallbackResponse;
  const { graph_data, yearly_forecast, year_th, overall_theme } = data;
  const userInfo =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const userId = userInfo ? JSON.parse(userInfo)._id : null;
  const [searchParams] = useSearchParams();
  const subCategoryId = searchParams.get("category");

  const [hoverInsight, setHoverInsight] = useState<Insight | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [tooltipSize, setTooltipSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [showBirthPopup, setShowBirthPopup] = useState(false);
  const [hasDOB, setHasDOB] = useState(false);
  const [showEnterDobMessage, setShowEnterDobMessage] = useState(false);
  const chartWrapRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const pinnedPopupRef = useRef<HTMLDivElement | null>(null);

  // Check for user DOB in localStorage and set hasDOB state
  useEffect(() => {
    const userInfo =
      typeof window !== "undefined" ? localStorage.getItem("user") : null;
    if (userInfo) {
      const user = JSON.parse(userInfo);
      const userDOB =
        user?.dob ||
        user?.date_of_birth ||
        user?.dateOfBirth ||
        user?.birthDate;
      if (userDOB && userDOB.trim() !== "") {
        setHasDOB(true);
      } else {
        setShowBirthPopup(true);
      }
    } else {
      setShowBirthPopup(true);
      setShowEnterDobMessage(false);
    }
  }, []);

  // Fetch forecast only when hasDOB is true
  useEffect(() => {
    let isMounted = true;
    const fetchForecast = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post("/backend/lifeGraph/create", {
          userId,
          subCategoryId,
        });
        const raw = response.data?.aiResponse ?? response.data;
        const parsed =
          typeof raw === "string"
            ? JSON.parse(
                raw
                  .replace(/```json/g, "")
                  .replace(/```/g, "")
                  .trim(),
              )
            : raw;
        if (!parsed || parsed.status !== "success") {
          throw new Error(parsed?.error || "");
        }
        if (isMounted) {
          setApiResponse(parsed as ForecastResponse);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(serverErrorMessage(err, "lifeGraph.forecast.loadFailed"));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Only call API if hasDOB is true
    if (hasDOB) {
      fetchForecast();
    }

    return () => {
      isMounted = false;
    };
  }, [hasDOB, userId, subCategoryId]);

  // Keep the pinned popup hidden until the user selects a point.

  useEffect(() => {
    if (!hoverInsight) return;
    const el = tooltipRef.current;
    if (!el) return;
    setTooltipSize({ w: el.offsetWidth, h: el.offsetHeight });
  }, [hoverInsight, hoverPos]);

  const chartData = useMemo(() => {
    return graph_data.months_labels.map((month, idx) => {
      const row: Record<string, string | number> = { month };
      graph_data.series.forEach((series, sIdx) => {
        row[`s${sIdx}`] = series.data[idx];
      });
      return row;
    });
  }, [graph_data]);

  const monthIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    graph_data.months_labels.forEach((label, idx) => {
      map.set(String(label).trim(), idx);
    });
    return map;
  }, [graph_data]);

  const allInsights = useMemo(() => {
    const items: Insight[] = [];
    graph_data.series.forEach((series, seriesIndex) => {
      if (series.peak_point) {
        items.push({
          title: series.peak_point.title,
          desc: series.peak_point.desc,
          color: series.color,
          month: series.peak_point.month,
          icon: seriesIndex === 1 ? "heart" : "crown",
          seriesLabel: series.label,
        });
      }
      if (series.low_point) {
        items.push({
          title: series.low_point.title,
          desc: series.low_point.desc,
          color: series.color,
          month: series.low_point.month,
          icon: "cloud",
          seriesLabel: series.label,
        });
      }
    });
    return items.sort((a, b) => {
      const aIdx = monthIndexMap.get(String(a.month).trim()) ?? 99;
      const bIdx = monthIndexMap.get(String(b.month).trim()) ?? 99;
      return aIdx - bIdx;
    });
  }, [graph_data, monthIndexMap]);

  const getPointIndex = (
    series: ForecastSeries,
    type: "peak" | "low",
  ): number | undefined => {
    const point = type === "peak" ? series.peak_point : series.low_point;
    if (!point) return undefined;
    const byMonth = monthIndexMap.get(String(point.month).trim());
    if (byMonth != null) return byMonth;
    const targetValue = Number(point.value);
    if (!Number.isFinite(targetValue)) return undefined;
    const idx = series.data.findIndex(
      (v) => Math.abs(Number(v) - targetValue) < 0.0001,
    );
    return idx >= 0 ? idx : undefined;
  };

  const maxBySeries = useMemo(
    () => graph_data.series.map((series) => Math.max(...series.data)),
    [graph_data],
  );

  // If the birth details popup is open, don't show fallback graph data.
  if (showBirthPopup && !apiResponse) {
    return (
      <div className="w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8">
        <BirthDetailsPopup
          isOpen={showBirthPopup}
          category="LifeGraph"
          onClose={() => {
            setShowBirthPopup(false);
            if (!hasDOB) {
              setShowEnterDobMessage(true);
            }
          }}
          onSave={(details) => {
            setShowBirthPopup(false);
            setHasDOB(true);
            setShowEnterDobMessage(false);
          }}
        />
      </div>
    );
  }

  if (!hasDOB && showEnterDobMessage) {
    return (
      <div className="w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8">
        <div className="mx-auto max-w-2xl rounded-3xl border border-gray-200 bg-gray-50 p-8 text-center shadow-sm">
          <h2 className="mb-3 text-2xl font-semibold text-gray-900">
            {t("lifeGps.page.dobTitle")}
          </h2>
          <p className="mb-6 text-gray-600">
            {t("lifeGraph.forecast.dobBody")}
          </p>
          <button
            type="button"
            className="rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            onClick={() => {
              setShowBirthPopup(true);
              setShowEnterDobMessage(false);
            }}
          >
            {t("lifeGps.page.dobCta")}
          </button>
        </div>
      </div>
    );
  }

  const handlePointHover = (
    seriesIndex: number,
    value: number,
    month: string,
    x: number,
    y: number,
  ) => {
    const series = graph_data.series[seriesIndex];
    const numericValue = Number(value);
    const monthIndex = monthIndexMap.get(String(month).trim());
    const peakIndex = getPointIndex(series, "peak");
    const lowIndex = getPointIndex(series, "low");
    const isPeak = peakIndex != null && monthIndex === peakIndex;
    const isLow = lowIndex != null && monthIndex === lowIndex;

    if (isPeak && series.peak_point) {
      setHoverInsight({
        title: series.peak_point.title,
        desc: series.peak_point.desc,
        color: series.color,
        month,
        icon: seriesIndex === 1 ? "heart" : "crown",
        seriesLabel: series.label,
      });
      setHoverPos({ x, y });
      return;
    }

    if (isLow && series.low_point) {
      setHoverInsight({
        title: series.low_point.title,
        desc: series.low_point.desc,
        color: series.color,
        month,
        icon: "cloud",
        seriesLabel: series.label,
      });
      setHoverPos({ x, y });
      return;
    }

    setHoverInsight(null);
    setHoverPos(null);
  };

  const handlePointSelect = (
    seriesIndex: number,
    value: number,
    month: string,
  ) => {
    const series = graph_data.series[seriesIndex];
    const numericValue = Number(value);
    const monthIndex = monthIndexMap.get(String(month).trim());
    const peakIndex = getPointIndex(series, "peak");
    const lowIndex = getPointIndex(series, "low");
    const isPeak = peakIndex != null && monthIndex === peakIndex;
    const isLow = lowIndex != null && monthIndex === lowIndex;

    if (isPeak && series.peak_point) {
      setSelectedInsight({
        title: series.peak_point.title,
        desc: series.peak_point.desc,
        color: series.color,
        month,
        icon: seriesIndex === 1 ? "heart" : "crown",
        seriesLabel: series.label,
      });
      return;
    }

    if (isLow && series.low_point) {
      setSelectedInsight({
        title: series.low_point.title,
        desc: series.low_point.desc,
        color: series.color,
        month,
        icon: "cloud",
        seriesLabel: series.label,
      });
      return;
    }

    setSelectedInsight(null);
  };

  const handleInsightDetails = () => {
    if (!hoverInsight) return;
    setSelectedInsight(hoverInsight);
    requestAnimationFrame(() => {
      pinnedPopupRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      pinnedPopupRef.current?.focus();
    });
  };

  const renderDot = (seriesIndex: number) => (props: any) => {
    const { cx, cy, value, payload } = props;
    if (cx == null || cy == null) return null;

    const series = graph_data.series[seriesIndex];
    const numericValue = Number(value);
    const month = payload?.month ?? "";
    const monthIndex = monthIndexMap.get(String(month).trim());
    const peakIndex = getPointIndex(series, "peak");
    const lowIndex = getPointIndex(series, "low");
    const isPeak = peakIndex != null && monthIndex === peakIndex;
    const isLow = lowIndex != null && monthIndex === lowIndex;
    const canShowPeak = isPeak && seriesIndex !== 2;
    const canShowLow = isLow && seriesIndex === 2;
    const isHoverable = canShowPeak || canShowLow;
    const handleEnter = (
      e: React.MouseEvent<SVGGElement | SVGCircleElement>,
    ) => {
      const rect = chartWrapRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        handlePointHover(seriesIndex, value, month, x, y);
        return;
      }
      handlePointHover(seriesIndex, value, month, cx, cy);
    };
    const handleMove = (
      e: React.MouseEvent<SVGGElement | SVGCircleElement>,
    ) => {
      const rect = chartWrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      setHoverPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };
    const handleLeave = () => {
      setHoverInsight(null);
      setHoverPos(null);
    };

    if (canShowPeak) {
      return (
        <g
          onMouseEnter={handleEnter}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          onClick={() => handlePointSelect(seriesIndex, value, month)}
          style={{ cursor: "pointer" }}
        >
          <circle cx={cx} cy={cy} r={6} fill={series.color} />
          <g transform={`translate(${cx - 10}, ${cy - 28})`}>
            {seriesIndex === 1 ? (
              <Heart width={20} height={20} color={series.color} />
            ) : (
              <Crown width={20} height={20} color={series.color} />
            )}
          </g>
        </g>
      );
    }

    if (canShowLow) {
      return (
        <g
          onMouseEnter={handleEnter}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          onClick={() => handlePointSelect(seriesIndex, value, month)}
          style={{ cursor: "pointer" }}
        >
          <circle cx={cx} cy={cy} r={5} fill={series.color} />
          <g transform={`translate(${cx - 10}, ${cy - 26})`}>
            <CloudRain width={20} height={20} color={series.color} />
          </g>
        </g>
      );
    }

    return (
      <circle
        cx={cx}
        cy={cy}
        r={3}
        fill={series.color}
        onMouseEnter={isHoverable ? handleEnter : undefined}
        onMouseMove={isHoverable ? handleMove : undefined}
        onMouseLeave={isHoverable ? handleLeave : undefined}
        onClick={
          isHoverable
            ? () => handlePointSelect(seriesIndex, value, month)
            : undefined
        }
      />
    );
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center text-center gap-4">
          <div className="relative h-16 w-16">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-500 border-r-purple-500 border-b-pink-500 animate-spin" />
            <div className="absolute inset-2 rounded-full bg-white shadow-inner" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">
              {t("lifeGraph.forecast.loadingTitle")}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {t("lifeGraph.forecast.loadingBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8">
        <div className="rounded-3xl border border-red-200 bg-white shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] p-6 sm:p-8 md:p-10 text-center text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8">
      <div className="rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-white to-slate-50 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] p-4 sm:p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-5 mb-5 sm:mb-6">
          <div>
            {/* <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-gradient-to-r from-amber-400 to-pink-500" />
              Astria Forecast
            </div> */}
            <h2 className="mt-2 sm:mt-3 text-xl sm:text-2xl md:text-4xl font-semibold text-slate-900 tracking-tight">
              {t("lifeGraph.forecast.title")}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-500">
                {year_th}
              </span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-2 max-w-2xl">
              {overall_theme}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm sm:text-base font-semibold text-slate-900">
              Astria
            </p>
            <AstriaRingLogo className="h-8 w-8 sm:h-9 sm:w-9" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {graph_data.series.map((series) => (
            <div
              key={series.label}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] sm:text-xs text-slate-600 shadow-sm"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.08)]"
                style={{ backgroundColor: series.color }}
              />
              <span>{series.label}</span>
            </div>
          ))}
        </div>

        <div
          ref={chartWrapRef}
          onMouseLeave={() => {
            setHoverInsight(null);
            setHoverPos(null);
          }}
          className="relative overflow-hidden border border-slate-200 rounded-3xl bg-white shadow-[0_20px_50px_-35px_rgba(15,23,42,0.45)]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 85% 0%, rgba(236,72,153,0.08), transparent 35%), linear-gradient(180deg, rgba(248,250,252,0.9), rgba(255,255,255,1))",
          }}
        >
          <div className="h-[360px] xs:h-[380px] sm:h-[420px] md:h-[480px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 16, left: -12, bottom: 0 }}
              >
                <defs>
                  {graph_data.series.map((series, idx) => (
                    <linearGradient
                      key={`area-${idx}`}
                      id={`area-${idx}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={series.color}
                        stopOpacity={0.18}
                      />
                      <stop
                        offset="100%"
                        stopColor={series.color}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  ))}
                  {graph_data.series.map((series, idx) => (
                    <filter
                      key={`glow-${idx}`}
                      id={`glow-${idx}`}
                      height="200%"
                      width="200%"
                      x="-50%"
                      y="-50%"
                    >
                      <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  ))}
                </defs>

                <CartesianGrid
                  strokeDasharray="4 6"
                  stroke="#E5E7EB"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#6B7280", fontSize: 11 }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tick={{ fill: "#9CA3AF", fontSize: 10 }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={false}
                />
                <Tooltip content={<></>} wrapperStyle={{ display: "none" }} />

                {graph_data.series.map((series, idx) => (
                  <Area
                    key={`area-${idx}`}
                    type="monotone"
                    dataKey={`s${idx}`}
                    stroke="none"
                    fill={`url(#area-${idx})`}
                    isAnimationActive={false}
                  />
                ))}

                {graph_data.series.map((series, idx) => (
                  <Line
                    key={`line-${idx}`}
                    type="monotone"
                    dataKey={`s${idx}`}
                    stroke={series.color}
                    strokeWidth={4}
                    dot={renderDot(idx)}
                    activeDot={false}
                    filter={`url(#glow-${idx})`}
                    isAnimationActive={true}
                    animationDuration={1200}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {hoverInsight && hoverPos && (
            <div
              ref={tooltipRef}
              className="absolute z-20 max-w-[280px] rounded-2xl bg-white/95 text-gray-900 p-3 shadow-[0_18px_45px_-25px_rgba(15,23,42,0.5)] border border-slate-200 backdrop-blur-sm"
              style={{
                left: (() => {
                  const rect = chartWrapRef.current?.getBoundingClientRect();
                  const maxW = rect?.width ?? 0;
                  const w = tooltipSize?.w ?? 260;
                  const x = hoverPos.x + 12;
                  return Math.min(Math.max(x, 8), Math.max(8, maxW - w - 8));
                })(),
                top: (() => {
                  const rect = chartWrapRef.current?.getBoundingClientRect();
                  const maxH = rect?.height ?? 0;
                  const h = tooltipSize?.h ?? 120;
                  const y = hoverPos.y + 12;
                  return Math.min(Math.max(y, 8), Math.max(8, maxH - h - 8));
                })(),
              }}
            >
              <div className="absolute -top-2 left-6 h-3 w-3 rotate-45 bg-white/95 border-l border-t border-slate-200" />
              <div className="flex items-start gap-2">
                <div
                  className="h-9 w-9 rounded-xl flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]"
                  style={{ backgroundColor: `${hoverInsight.color}20` }}
                >
                  {hoverInsight.icon === "crown" ? (
                    <Crown width={16} height={16} color={hoverInsight.color} />
                  ) : hoverInsight.icon === "heart" ? (
                    <Heart width={16} height={16} color={hoverInsight.color} />
                  ) : (
                    <CloudRain
                      width={16}
                      height={16}
                      color={hoverInsight.color}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-500">
                    {hoverInsight.month}
                  </p>
                  <div className="text-sm font-semibold text-slate-900 leading-snug">
                    <ReactMarkdown components={markdownComponents}>
                      {hoverInsight.title}
                    </ReactMarkdown>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 leading-relaxed line-clamp-3">
                    <ReactMarkdown components={markdownComponents}>
                      {hoverInsight.desc}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  onClick={handleInsightDetails}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  {t("lifeGraph.forecast.details")}
                  <span className="text-slate-400">→</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          ref={pinnedPopupRef}
          tabIndex={-1}
          className={`mt-4 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm transition-opacity ${
            selectedInsight ? "opacity-100" : "opacity-0"
          }`}
        >
          {selectedInsight ? (
            <div className="flex items-start gap-3">
              <div
                className="h-8 w-8 sm:h-9 sm:w-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${selectedInsight.color}20` }}
              >
                {selectedInsight.icon === "crown" ? (
                  <Crown width={18} height={18} color={selectedInsight.color} />
                ) : selectedInsight.icon === "heart" ? (
                  <Heart width={18} height={18} color={selectedInsight.color} />
                ) : (
                  <CloudRain
                    width={18}
                    height={18}
                    color={selectedInsight.color}
                  />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-500">
                  {selectedInsight.month}
                  {selectedInsight.seriesLabel
                    ? ` · ${selectedInsight.seriesLabel}`
                    : ""}
                </p>
                <div className="text-sm sm:text-base font-semibold text-gray-900">
                  <ReactMarkdown components={markdownComponents}>
                    {selectedInsight.title}
                  </ReactMarkdown>
                </div>
                <div className="mt-1">
                  <ReactMarkdown components={markdownComponents}>
                    {selectedInsight.desc}
                  </ReactMarkdown>
                </div>
              </div>
              <button
                onClick={() => setSelectedInsight(null)}
                className="ml-auto text-gray-400 hover:text-gray-600"
                aria-label={t("common.close")}
              >
                ✕
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              {t("lifeGraph.forecast.pinHint")}
            </p>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white/90 p-4 sm:p-5 backdrop-blur">
          <div className="mt-4 grid grid-cols-1 gap-3">
            {allInsights.map((insight, idx) => (
              <div
                key={`${insight.month}-${insight.title}-${idx}`}
                className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50 p-3 shadow-[0_16px_40px_-30px_rgba(15,23,42,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-32px_rgba(15,23,42,0.45)]"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-9 w-9 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${insight.color}20` }}
                  >
                    {insight.icon === "crown" ? (
                      <Crown width={16} height={16} color={insight.color} />
                    ) : insight.icon === "heart" ? (
                      <Heart width={16} height={16} color={insight.color} />
                    ) : (
                      <CloudRain width={16} height={16} color={insight.color} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-slate-500">
                      {insight.month}
                      {insight.seriesLabel ? ` · ${insight.seriesLabel}` : ""}
                    </p>
                    <div className="text-sm font-semibold text-slate-900">
                      <ReactMarkdown components={markdownComponents}>
                        {insight.title}
                      </ReactMarkdown>
                    </div>
                    <div className="mt-1 text-[16px] text-slate-600 leading-relaxed">
                      <ReactMarkdown components={markdownComponents}>
                        {insight.desc}
                      </ReactMarkdown>
                    </div>
                    {/* <div className="mt-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      <span
                        className="h-1.5 w-6 rounded-full"
                        style={{ backgroundColor: insight.color }}
                      />
                      Highlight
                    </div> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-4 md:p-5">
          <h3 className="text-sm sm:text-base font-semibold mb-3 text-slate-900">
            Yearly Forecast
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm text-slate-700">
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">
                Peak Period
              </p>
              <p>{yearly_forecast.peak_period}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">
                Obstacle Period
              </p>
              <p>{yearly_forecast.obstacle_period}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">
                Auspicious Colors
              </p>
              <p>{yearly_forecast.auspicious_colors.join(", ")}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">
                Lucky Numbers
              </p>
              <p>{yearly_forecast.lucky_numbers.join(", ")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Birth Details Popup */}
      {showBirthPopup && (
        <BirthDetailsPopup
          isOpen={showBirthPopup}
          category="LifeGraph"
          onClose={() => setShowBirthPopup(false)}
          onSave={(details) => {
            setShowBirthPopup(false);
            setHasDOB(true);
          }}
        />
      )}
    </div>
  );
};

export default LifeGraph;
const markdownComponents = {
  h1: (props: any) => (
    <h1 className="text-sm font-semibold text-gray-900 mb-1">
      {props.children}
    </h1>
  ),
  h2: (props: any) => (
    <h2 className="text-sm font-semibold text-gray-900 mb-1">
      {props.children}
    </h2>
  ),
  h3: (props: any) => (
    <h3 className="text-sm font-semibold text-gray-900 mb-1">
      {props.children}
    </h3>
  ),
  p: (props: any) => (
    <p className="text-[16px] text-gray-600 mt-1">{props.children}</p>
  ),
  strong: (props: any) => (
    <strong className="font-semibold text-gray-900">{props.children}</strong>
  ),
};
